import { Box, Grid, Typography } from "@mui/material";
import * as React from "react";
import { Translations } from "ui/Scripts/source/models/translations";
import { IUnitDefaultHandlersNode, UnitDefaultHandlersNode } from "../../../../models/unit/unitDefaultHandlersNode";
import { IUnitDefaultHandlersNodeUser } from "../../../../models/unit/unitDefaultHandlersNodeUser";
import { UserListItems } from "../../../../models/user/userListItems";
import { ActionContainer } from "../../../framework/ActionContainer";
import { DialogSettingsCard } from "../../../framework/Container/DialogSettingsCard";
import { ListIconButtonsPermission } from "../../../framework/List/ListIconButtons/ListIconButtonsPermission";
import { UnitDefaultHandlersEditDialog } from "../UnitDefaultHandlersEditDialog";
import { UnitDefaultHandlersTree } from "../UnitDefaultHandlersTree";
import { AppUtils } from "source/framework/appUtils";
import { DefaultHandlerType } from "ui/Scripts/source/models/enums";
import { VerticalContainer } from "ui/Scripts/source/components/framework/VerticalContainer";

export interface IUnitDefaultHandlersProp {
    classes?: string;
    infoText?: string;
    companyId: string;
    unitId: string;
    disabled?: boolean;
    showHandlerTypes?: boolean;
    unitNode: IUnitDefaultHandlersNode;
    partnerCompanyIds?: string[];
    onChangeSaveUnitNode: (saveUnitNode: IUnitDefaultHandlersNode) => void;
    onGetUserSelectListItems?: (companyId: string, unitId: string, handlerType: DefaultHandlerType, pageSize: number, page: number, filter: string,
        ignoreUserIds: string[], ignoreUserCodes: string[], activityState: number, ignoreRecordUserConnected: boolean,
        doNotIgnoreRecordUserConnectedUserCode: string, sortColumn: string, sortOrderIsAsc: boolean) => Promise<UserListItems>;
}

interface IUnitDefaultHandlersState {
    copiedDefaultHandlers: IUnitDefaultHandlersNodeUser[];
    unitNode: IUnitDefaultHandlersNode;
    displayUnitNode: IUnitDefaultHandlersNode;
    editUnitNode: IUnitDefaultHandlersNode;
    selectedUnitNode: IUnitDefaultHandlersNode;
    showUnitDefaultHandlersEditDialog: boolean;
}

export class UnitDefaultHandlers extends React.Component<IUnitDefaultHandlersProp, IUnitDefaultHandlersState> {
    constructor(props: IUnitDefaultHandlersProp) {
        super(props);
        const unitNode = new UnitDefaultHandlersNode(props.unitNode);
        this.state = {
            unitNode,
            displayUnitNode: unitNode.getUnitNodeById(props.unitId),
            editUnitNode: null,
            selectedUnitNode: unitNode.getUnitNodeById(props.unitId),
            copiedDefaultHandlers: [],
            showUnitDefaultHandlersEditDialog: false
        };
    }

    editDefaultHandlers = (id: string) => {
        const state = this.state;
        if (!id) return;
        const editUnitNode = state.unitNode.getUnitNodeById(id);
        if (!editUnitNode) return;
        this.setState({
            showUnitDefaultHandlersEditDialog: true,
            editUnitNode
        });
    };

    handleUnitNodeClick = (id: string) => {
        if (!id) return;
        this.setState({
            selectedUnitNode: this.state.unitNode.getUnitNodeById(id)
        });
    };

    handleEditPermission = (id: string) => {
        this.editDefaultHandlers(id);
    };

    copyDefaultHandlersToSiblings = (unitNode: IUnitDefaultHandlersNode, handlers: IUnitDefaultHandlersNodeUser[]) => {
        if (!unitNode.hasSiblings()) return;
        const siblings = unitNode.getSiblings();
        for (const sibling of siblings) {
            for (const handler of handlers) {
                sibling.setUser(handler.code, handler.type, handler.name, handler.picture, false);
            }
        }
    };

    handleUnitDefaultHandlersEditDialogOk = (handlers: IUnitDefaultHandlersNodeUser[], clearSubUnitHandlers: boolean, copyHandlersToAllSameLevelUnits: boolean) => {
        const state = this.state;
        const props = this.props;
        AppUtils.runLongTask(() => {
            const unitNode = new UnitDefaultHandlersNode(state.unitNode);
            const editUnitNode = unitNode.getUnitNodeById(state.editUnitNode.id);
            if (!editUnitNode) return;
            editUnitNode.removeUsers(clearSubUnitHandlers);
            for (const handler of handlers) {
                editUnitNode.setUser(handler.code, handler.type, handler.name, handler.picture, false);
            }
            if (copyHandlersToAllSameLevelUnits) {
                this.copyDefaultHandlersToSiblings(editUnitNode, handlers);
            }
            const displayUnitNode = unitNode.getUnitNodeById(props.unitId);
            this.setState({
                showUnitDefaultHandlersEditDialog: false,
                unitNode,
                displayUnitNode: unitNode.getUnitNodeById(props.unitId),
            });
            props.onChangeSaveUnitNode(displayUnitNode);
            return unitNode;
        });
    };

    handleUnitDefaultHandlersEditDialogCancel = () => {
        this.setState({
            showUnitDefaultHandlersEditDialog: false
        });
    };

    handleUnitDefaultHandlersEditDialogRemove = () => {
        const state = this.state;
        const props = this.props;
        if (!state.selectedUnitNode) return;
        const unitNode = new UnitDefaultHandlersNode(state.unitNode);
        const editUnitNode = unitNode.getUnitNodeById(state.selectedUnitNode?.id);
        if (!editUnitNode) return;
        editUnitNode.removeUsers(false);
        const displayUnitNode = unitNode.getUnitNodeById(props.unitId);
        this.setState({
            showUnitDefaultHandlersEditDialog: false,
            unitNode,
            displayUnitNode,
        });
        props.onChangeSaveUnitNode(displayUnitNode);
    };

    handleCopyDefaultHandlers = () => {
        const state = this.state;
        if (!state.selectedUnitNode) return;
        const displayUnitNode = state.displayUnitNode;
        if (!displayUnitNode) return;
        const modifyUnitNode = displayUnitNode.getUnitNodeById(state.selectedUnitNode?.id);
        if (!modifyUnitNode || !modifyUnitNode.hasExplicitUsers()) return;
        this.setState({
            copiedDefaultHandlers: modifyUnitNode.getUsers()
        });
    };

    handlePasteDefaultHandlers = () => {
        const obj = this;
        const props = this.props;
        const state = this.state;
        if (!state.selectedUnitNode || !state.copiedDefaultHandlers.length) return;
        const unitNode = new UnitDefaultHandlersNode(state.unitNode);
        const displayUnitNode = unitNode.getUnitNodeById(props.unitId);
        if (!displayUnitNode) return;
        return AppUtils.runLongTask(() => {
            const modifyUnitNode = unitNode.getUnitNodeById(state.selectedUnitNode?.id);
            modifyUnitNode.removeUsers(true);
            for (const user of state.copiedDefaultHandlers) {
                modifyUnitNode.setUser(user.code, user.type, user.name, user.picture, false);
            }
            obj.setState({
                unitNode,
                displayUnitNode
            });
            props.onChangeSaveUnitNode(displayUnitNode);
            return unitNode;
        });
    };

    render() {
        const props = this.props;
        const state = this.state;
        return (
            <Box display="flex" flexDirection="column" paddingBottom="1.9rem" flexGrow="1">
                <DialogSettingsCard
                    fitToContainerHeight
                    title={props.showHandlerTypes ? Translations.DefaultEventHandlersAndNotifiedPersons : Translations.DefaultHandlers}
                    info={props.infoText}
                    cardContent={
                        <ActionContainer
                            rightItems={
                                <ListIconButtonsPermission
                                    editTooltip={Translations.EditDefaultHandlers}
                                    copyTooltip={Translations.CopyDefaultHandlers}
                                    pasteTooltip={Translations.PasteDefaultHandlers}
                                    editDisabled={props.disabled}
                                    copyDisabled={props.disabled || !(state.selectedUnitNode?.hasExplicitUsers() ?? false)}
                                    pasteDisabled={props.disabled || !state.copiedDefaultHandlers.length}
                                    onEdit={() => this.handleEditPermission(state.selectedUnitNode?.id ?? "")}
                                    onCopy={this.handleCopyDefaultHandlers}
                                    onPaste={this.handlePasteDefaultHandlers}
                                />
                            }
                        />
                    }
                    content={
                        <Box sx={{ paddingBottom: ".5rem", height: "100%" }}>
                            <VerticalContainer
                                before={
                                    <Grid container sx={{ backgroundColor: "action.hover", borderBottom: "1px solid", borderColor: "divider", padding: "0 1rem" }}>
                                        <Grid item sm={5}>
                                            <Box sx={{ cursor: "default", padding: "9px 0" }}>
                                                <Typography>{Translations.Unit}</Typography>
                                            </Box>
                                        </Grid>
                                        <Grid item sm={7}>
                                            <Grid container>
                                                <Grid item sm={props.showHandlerTypes ? 6 : 12}>
                                                    <Box sx={{ cursor: "default", padding: "9px 0" }}>
                                                        <Typography>{Translations.DefaultHandlers}</Typography>
                                                    </Box>
                                                </Grid>
                                                {!!props.showHandlerTypes &&
                                                    <Grid item sm={6}>
                                                        <Box sx={{ cursor: "default", padding: "9px 0" }}>
                                                            <Typography>{Translations.NotifiedPersonsShort}</Typography>
                                                        </Box>
                                                    </Grid>
                                                }
                                            </Grid>
                                        </Grid>
                                    </Grid>
                                }
                                main={
                                    <UnitDefaultHandlersTree
                                        unitNode={state.displayUnitNode}
                                        selectedId={state.selectedUnitNode?.id ?? ""}
                                        isFirstNode={true}
                                        height="100%"
                                        showHandlerTypes={props.showHandlerTypes}
                                        onClick={this.handleUnitNodeClick}
                                        onOpen={this.handleEditPermission}
                                    />
                                }
                                after={<>
                                    {(props.partnerCompanyIds ?? []).length > 0 &&
                                        <Box display="flex" alignItems="center">
                                            <Typography color="error" sx={{ margin: "0 .5rem" }}>*</Typography>
                                            <Typography>{Translations.UnitIsRelatedToEventPartner}</Typography>
                                        </Box>
                                    }
                                </>}
                            />
                        </Box>
                    }
                />
                {state.showUnitDefaultHandlersEditDialog &&
                    <UnitDefaultHandlersEditDialog
                        companyId={props.companyId}
                        showHandlerTypes={props.showHandlerTypes}
                        unitNode={state.editUnitNode}
                        onOk={this.handleUnitDefaultHandlersEditDialogOk}
                        onRemove={this.handleUnitDefaultHandlersEditDialogRemove}
                        onCancel={this.handleUnitDefaultHandlersEditDialogCancel}
                        onGetUserSelectListItems={props.onGetUserSelectListItems}
                    />
                }
            </Box>
        );
    }
}